import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/components/View';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getPageViewedEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY as GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServePublicRpc.dfc.new';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServeRpc.dfc.new';
import { UmbrellaCollectionCard } from '../collections/components/UmbrellaCollectionCard';
import { StorefrontDashboardWithTabs } from '../components/StorefrontDashboardWithTabs';
import { StorefrontPageMetadata } from '../components/StorefrontPageMetadata';
import { UnifiedHeader } from '../components/headers/UnifiedHeader';
import { Screens } from '../constants/usage';
import { useGates } from '../hooks/useGates';
import { useAppContextQuery } from '../hooks/useAppContextQuery';
import { FeaturedCollectionCard } from './components/FeaturedCollectionCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function FeaturedPage() {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    screen: Screens.FEATURED,
    children: /*#__PURE__*/_jsx(FeaturedPageContent, {})
  });
}
export function FeaturedPageContent() {
  var _data$result2;
  const {
    data,
    loading,
    error
  } = useAppContextQuery({
    app: GET_ALL_PUBLISHED_COLLECTIONS_QUERY,
    public: GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC
  });
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const gates = useGates();
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      lg: false
    }
  });
  const collections = useMemo(() => {
    var _data$result;
    return (data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.collections) === null || _data$result === void 0 ? void 0 : _data$result.filter(collection => collection.displayed !== false).sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))) || [];
  }, [data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 ? void 0 : _data$result2.collections]);
  useEffect(() => {
    trackMarketplaceEvent(getPageViewedEvent({}));
  }, [trackMarketplaceEvent]);
  const content = loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "medium",
    marginBlockStart: "800"
  }) : error ? /*#__PURE__*/_jsx(ErrorMessage, {
    type: "badRequest"
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(0, 2).map((collection, index) => /*#__PURE__*/_jsx(FeaturedCollectionCard, {
        collection: collection,
        index: index,
        resultCount: collections.length
      }, `${collection.slug}-${index}`))
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "400",
      children: /*#__PURE__*/_jsx(UmbrellaCollectionCard, {
        title: unescapedText('marketplaceStorefront.umbrellaCollections.aiEssentials.title'),
        description: unescapedText(gates.workflowTemplatesPhase2 ? 'marketplaceStorefront.umbrellaCollections.aiEssentials.descriptionRebranded' : 'marketplaceStorefront.umbrellaCollections.aiEssentials.description'),
        slug: "ai-essentials"
      })
    }), /*#__PURE__*/_jsx(CardGrid, {
      columns: isMobile ? 1 : 2,
      gap: "400",
      children: collections.slice(2).map((collection, index) => /*#__PURE__*/_jsx(FeaturedCollectionCard, {
        collection: collection,
        index: index,
        resultCount: collections.length
      }, `${collection.slug}-${index}`))
    })]
  });
  if (gates.newDashboard || gates.workflowTemplatesPhase2) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [!!data && /*#__PURE__*/_jsx(NavMarker, {
        name: "FEATURED_LOAD"
      }), /*#__PURE__*/_jsx(StorefrontPageMetadata, {
        loading: loading,
        title: unescapedText('marketplaceStorefront.documentHead.featured.title'),
        metaDescription: unescapedText('marketplaceStorefront.documentHead.featured.metaDescription')
      }), /*#__PURE__*/_jsx(StorefrontDashboardWithTabs, {
        children: content
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!!data && /*#__PURE__*/_jsx(NavMarker, {
      name: "FEATURED_LOAD"
    }), /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      loading: loading,
      title: unescapedText('marketplaceStorefront.documentHead.featured.title'),
      metaDescription: unescapedText('marketplaceStorefront.documentHead.featured.metaDescription')
    }), /*#__PURE__*/_jsx(DashboardPage, {
      headerComponent: /*#__PURE__*/_jsx(UnifiedHeader, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplaceStorefront.featured.title"
        }),
        pathKey: "FEATURED",
        dataTestId: "featured-page-header",
        isHomePage: false
      }),
      children: content
    })]
  });
}